import * as Sentry from '@sentry/browser'
import { useQuery } from 'react-query'

import { CSRF } from '../utils/CSRF'
import { BaseRepository } from './BaseRepository'

const defaultGlobalPresets: Resemble.GlobalPreset[] = [
  {
    id: -1,
    team_id: null,
    name: 'Angry',
    kind: 'global',
    blob: [
      { slug: 'pitch', start: 40, end: 20, controlPoint1: { x: 35, y: 0 }, controlPoint2: { x: -35, y: 0 } },
      { slug: 'intsy', start: 60, end: 90, controlPoint1: { x: 35, y: 0 }, controlPoint2: { x: -35, y: 0 } },
      { slug: 'pace', start: 30, end: 50, controlPoint1: { x: 35, y: 0 }, controlPoint2: { x: -35, y: 0 } },
    ],
  },
  {
    id: -2,
    team_id: null,
    name: 'Annoyed',
    kind: 'global',
    blob: [
      { slug: 'pitch', start: 20, end: 20, controlPoint1: { x: 35, y: 0 }, controlPoint2: { x: -35, y: 0 } },
      { slug: 'intsy', start: 60, end: 70, controlPoint1: { x: 35, y: 0 }, controlPoint2: { x: -35, y: 0 } },
      { slug: 'pace', start: 50, end: 70, controlPoint1: { x: 35, y: 0 }, controlPoint2: { x: -35, y: 0 } },
    ],
  },
  {
    id: -3,
    team_id: null,
    name: 'Question',
    kind: 'global',
    blob: [
      { slug: 'pitch', start: 60, end: 90, controlPoint1: { x: 35, y: 0 }, controlPoint2: { x: -35, y: 0 } },
      { slug: 'intsy', start: 60, end: 40, controlPoint1: { x: 35, y: 0 }, controlPoint2: { x: -35, y: 0 } },
      { slug: 'pace', start: 60, end: 30, controlPoint1: { x: 35, y: 0 }, controlPoint2: { x: -35, y: 0 } },
    ],
  },
  {
    id: -4,
    team_id: null,
    name: 'Happy',
    kind: 'global',
    blob: [
      { slug: 'pitch', start: 80, end: 90, controlPoint1: { x: 35, y: 0 }, controlPoint2: { x: -35, y: 0 } },
      { slug: 'intsy', start: 30, end: 50, controlPoint1: { x: 35, y: 0 }, controlPoint2: { x: -35, y: 0 } },
      { slug: 'pace', start: 60, end: 80, controlPoint1: { x: 35, y: 0 }, controlPoint2: { x: -35, y: 0 } },
    ],
  },
]

const defaultWordPresets: Resemble.WordPreset[] = [
  {
    id: -1,
    kind: 'word',
    team_id: null,
    name: 'Angry',
    blob: [
      { slug: 'pitch', value: 20 },
      { slug: 'intsy', value: 90 },
      { slug: 'pace', value: 90 },
    ],
  },
  {
    id: -2,
    kind: 'word',
    team_id: null,
    name: 'Annoyed',
    blob: [
      { slug: 'pitch', value: 40 },
      { slug: 'intsy', value: 90 },
      { slug: 'pace', value: 90 },
    ],
  },
  {
    id: -3,
    kind: 'word',
    team_id: null,
    name: 'Question',
    blob: [
      { slug: 'pitch', value: 90 },
      { slug: 'intsy', value: 40 },
      { slug: 'pace', value: 40 },
    ],
  },
  {
    id: -4,
    kind: 'word',
    team_id: null,
    name: 'Happy',
    blob: [
      { slug: 'pitch', value: 90 },
      { slug: 'intsy', value: 50 },
      { slug: 'pace', value: 20 },
    ],
  },
]

export class PresetRepository extends BaseRepository {
  addPreset(team: string, preset: Resemble.Preset) {
    return super.getHttpClientInstance().post(
      `/teams/${team}/presets.json`,
      {
        preset: {
          ...preset,
        },
      },
      {
        'X-CSRF-Token': CSRF.getToken(),
      },
    )
  }

  static getUniquePresetName(presets: Resemble.Preset[], kind: Resemble.PresetKind, name: string) {
    if (presets.find((p) => p.name === name && p.kind === kind)) {
      let i: number | undefined = undefined
      while (presets.find((p) => p.kind === kind && p.name === `${name}${i === undefined ? '' : ` ${i}`}`)) {
        i = i === undefined ? 2 : i + 1
      }
      return `${name}${i === undefined ? '' : ` ${i}`}`
    }

    return name
  }
}

type UsePresets = {
  wordPresets: Resemble.WordPreset[]
  globalPresets: Resemble.GlobalPreset[]
}

export const usePresets = (team: string) => {
  return useQuery<any, any, UsePresets, [string, string]>(
    ['presets', team],
    async () => {
      const response = await fetch(`/teams/${team}/presets.json`, {
        headers: {
          'X-CSRF-Token': CSRF.getToken(),
        },
      })
      try {
        const savedPresets: Array<Resemble.WordPreset | Resemble.GlobalPreset> = (await response.json()).map((p: Resemble.Preset) => {
          return {
            ...p,
            blob: typeof p.blob === 'string' ? JSON.parse(p.blob) : p.blob,
          }
        })

        return {
          wordPresets: [...defaultWordPresets, ...(savedPresets.filter((s) => s.kind === 'word') as Resemble.WordPreset[])],
          globalPresets: [...defaultGlobalPresets, ...(savedPresets.filter((s) => s.kind === 'global') as Resemble.GlobalPreset[])],
        }
      } catch (e) {
        Sentry.withScope((scope) => {
          scope.setExtras({
            team: team,
          })
          Sentry.captureException('Fetching presets failed')
        })
        return {
          wordPresets: [...defaultWordPresets],
          globalPresets: [...defaultGlobalPresets],
        }
      }
    },
    {
      refetchOnWindowFocus: false,
    },
  )
}
